import React from 'react';
import { Bar } from 'react-chartjs-2';
import { connect } from 'dva';
import { Button, Col, Row, Spin, Switch } from 'antd';
import { Typography } from '@hulu/react-style-components';
import styled from 'styled-components';

import { COUNT_TYPES, TOP_NUM_SELECTED, VIEW } from '../../../constants';
import { kebabCaseToSentenceCase } from '../../../utils';
import {
  VASTTRACKER_BARCHART,
  VASTTRACKER_BARCHART_TITLE,
  VASTTRACKER_BARCHART_TOPNUMSUFFIX,
} from '../../../testUtils';
import { ALL_AD_UNIT_IDS } from '../../../models/constants';
import { STATUS_COLORS, STATUS_NAMES } from '../constants';

const BAR_HEIGHT = 110;
const EVENT_TYPE_KEY = 'event-type';
const EVENT_TYPE_FAILURE = 'failure';
export const ButtonWrapper = ({ children, ...rest }) => <StyledButtons {...rest}>{children}</StyledButtons>;
const StyledButtons = styled(Button)`
  margin: 0 10px;
`;

const ALLOWED_EVENT_TYPES = new Set([
  // Existing event types
  STATUS_NAMES.SUCCESS,
  STATUS_NAMES.UNSUPPORTED_FORMAT,
  STATUS_NAMES.UNSUPPORTED_FORMAT_VPAID,
  STATUS_NAMES.UNSUPPORTED_NON_SSL,
  STATUS_NAMES.UNPARSEABLE_DOCUMENT,
  STATUS_NAMES.UNPARSEABLE_DOCUMENT_NULL_VAST,
  STATUS_NAMES.UNPARSEABLE_DOCUMENT_PARSE_FAIL,
  STATUS_NAMES.INVALID_DOCUMENT,
  STATUS_NAMES.INVALID_DOCUMENT_NO_CREATIVES,
  STATUS_NAMES.INVALID_DOCUMENT_INVALID_IMPRESSION_URL,
  STATUS_NAMES.INVALID_DOCUMENT_NO_TRACKING_EVENTS,
  STATUS_NAMES.INVALID_DOCUMENT_NO_VIDEO_CLICKS,
  STATUS_NAMES.MISSING_ASSET,
  STATUS_NAMES.MISSING_ASSET_EXTERNAL_AD,
  STATUS_NAMES.MISSING_ASSET_VAST_AD,
  STATUS_NAMES.INTERNAL_ERROR,
  STATUS_NAMES.INTERNAL_ERROR_INLINE_VAST_PROCESS,
  STATUS_NAMES.ASSET_REJECTION,
  STATUS_NAMES.ASSET_PUBLISHER_UNAPPROVED,
  STATUS_NAMES.PROGRAMMATIC_GUARANTEED_DEAL_MISMATCH,
  STATUS_NAMES.MISSING_ASSET_NOT_LINKED_TO_AD,
  // New event types (KOALA-1695)
  STATUS_NAMES.ASSET_MISSING,
  STATUS_NAMES.ASSET_MISSING_NOT_LINKED_TO_AD,
  STATUS_NAMES.ASSET_REJECTED,
  STATUS_NAMES.ASSET_UNQUALIFIED,
  STATUS_NAMES.COMPANION_INVALID_API_FRAMEWORK,
  STATUS_NAMES.COMPANION_INVALID_TYPE,
  STATUS_NAMES.COMPANION_MISSING,
  STATUS_NAMES.COMPANION_REGISTERED_NOT_FOUND,
  STATUS_NAMES.FETCH_FAILED,
  STATUS_NAMES.INTERNAL_INVALID_VAST_URL,
  STATUS_NAMES.INTERNAL_NO_VAST_URL,
  STATUS_NAMES.INTERNAL_NULL_VAST_FROM_AD_PROVIDER,
  STATUS_NAMES.INVALID_DOCUMENT_EMPTY_AD,
  STATUS_NAMES.INVALID_DOCUMENT_INVALID_CLICKTHROUGH_URL,
  STATUS_NAMES.INVALID_DOCUMENT_NULL_RAW,
  STATUS_NAMES.INVALID_DOCUMENT_PG_DEAL_MISMATCH,
  STATUS_NAMES.INVALID_DOCUMENT_PG_DEAL_NAME_MISSING,
  STATUS_NAMES.INVALID_DOCUMENT_UNMARSHALLER_EXCEPTION,
  STATUS_NAMES.INVALID_DOCUMENT_UNPARSEABLE_DOCUMENT,
  STATUS_NAMES.INVALID_DOCUMENT_UNWRAPPING_FAILURE,

  // Third-party event type that should be included here, consistent with its usage elsewhere
  STATUS_NAMES.LOCAL_PENALTY,
]);

const BarChart = ({
  dispatch,
  permissions,
  loading,
  filters = [],
  selectedCounts = [],
  topNumSelected,
  countTypeSelected = 'N/A',
  startDate = 'N/A',
  endDate = 'N/A',
  dailyViewEnabled,
}) => {
  let labels = [];
  let statuses = {};

  // Deduplicate based on "value" key.   This is oneeded for adunitid because we want to break out counts
  // by app in the table but not in the bar chart.
  selectedCounts = consolidateByValue(selectedCounts);
  if (selectedCounts && !dailyViewEnabled) {
    selectedCounts = selectedCounts.sort((a, b) => {
      const aFailure = a.counts.find((c) => c[EVENT_TYPE_KEY] === 'failure')?.occurrence || 0;
      const bFailure = b.counts.find((c) => c[EVENT_TYPE_KEY] === 'failure')?.occurrence || 0;
      return bFailure - aFailure;
    });
  }

  selectedCounts.forEach(({ value, counts }, index) => {
    labels.push(value);
    counts.forEach(({ 'event-type': eventType, occurrence }) => {
      if (!ALLOWED_EVENT_TYPES.has(eventType)) return;
      if (!(eventType in statuses)) statuses[eventType] = [];
      statuses[eventType][index] = occurrence;
    });
  });

  const data = Object.entries(statuses).map(([statusName, countNumbers]) => ({
    label: statusName,
    data: countNumbers.map((val) => val ?? 0),
    backgroundColor: STATUS_COLORS[statusName],
  }));

  const totals = data.reduce((acc, row) => {
    row.data.forEach((value, index) => {
      acc[index] = (acc[index] || 0) + (value || 0);
    });
    return acc;
  }, []);

  const options = {
    responsive: true,
    scales: {
      y: {
        stacked: true,
        ticks: {
          beginAtZero: true,
        },
      },
      x: {
        stacked: true,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label;
            const value = context.parsed.y;
            const total = totals[context.dataIndex];
            const percentage = total === 0 ? 'N/A' : ((value / total) * 100).toFixed(2) + '%';
            return `${label}: ${value} (${percentage})`;
          },
        },
      },
    },
  };

  const setDailyViewEnabled = (dailyViewEnabled) => {
    dispatch({
      type: 'vastTracker/setDailyViewEnabled',
      payload: { type: countTypeSelected, dailyViewEnabled },
    });
  };

  const topNumButtons = Object.entries(TOP_NUM_SELECTED).map(([_, num]) => (
    <ButtonWrapper
      style={topNumSelected === num ? { backgroundColor: '#0072ed' } : {}}
      key={`top_${num}`}
      type="primary"
      shape="round"
      size="small"
      onClick={() => {
        dispatch({ type: 'vastTracker/updateTopNumSelected', payload: num });
        dispatch({ type: 'vastTracker/pageInit' });
      }}
      data-testid={VASTTRACKER_BARCHART_TOPNUMSUFFIX + '_' + num}
    >
      {`Top ${num}`}
    </ButtonWrapper>
  ));

  const getTitleForBarChart = () => {
    return filters.length > 0
      ? `Failure Counts from ${startDate} to ${endDate}`
      : `Top ${topNumSelected} ${kebabCaseToSentenceCase(
          countTypeSelected
        )} by Failure Counts from ${startDate} to ${endDate}`;
  };

  return (
    <Spin spinning={loading}>
      <Row justify="space-between">
        {countTypeSelected !== COUNT_TYPES.ADUNITID || permissions.can(VIEW, ALL_AD_UNIT_IDS) ? (
          <>
            <Col>
              <Typography style={{ color: '#090699' }} variant="h5" data-testid={VASTTRACKER_BARCHART_TITLE}>
                {getTitleForBarChart()}
              </Typography>
            </Col>
            <Col span={6}>{filters.length > 0 ? null : topNumButtons}</Col>
            <Col span={4} align="right">
              <Typography variant="h8">Daily View</Typography>
              <Switch
                style={{ marginLeft: 5 }}
                checkedChildren="On"
                unCheckedChildren="Off"
                defaultChecked={dailyViewEnabled}
                onChange={setDailyViewEnabled}
              />
            </Col>
          </>
        ) : (
          <Col span={14}>
            <Typography style={{ color: '#090699' }} variant="h5" data-testid={VASTTRACKER_BARCHART_TITLE}>
              To View Data, Filter by Ad Unit Id to View Failure Counts from {startDate} to {endDate}
            </Typography>
          </Col>
        )}
      </Row>
      <br />
      <Bar
        data={{ labels: labels, datasets: data }}
        options={options}
        height={BAR_HEIGHT}
        data-testid={VASTTRACKER_BARCHART}
      />
    </Spin>
  );
};

/**
 * Consolidates an array of objects by the "value" key
 * and sums counts for matching "event-type".
 *
 * @param {Array} data - The input array of objects
 * @returns {Array} consolidated list
 */
function consolidateByValue(data) {
  return Object.values(
    data.reduce((acc, obj) => {
      const key = obj.value;
      if (!acc[key]) {
        acc[key] = {
          value: obj.value,
          counts: [],
          dailyRecords: {}, // Merge logic can be added here
        };
      }
      obj.counts.forEach((countItem) => {
        const existing = acc[key].counts.find((c) => c[EVENT_TYPE_KEY] === countItem[EVENT_TYPE_KEY]);
        if (existing) {
          existing.occurrence += countItem.occurrence;
        } else {
          acc[key].counts.push({ ...countItem });
        }
      });
      return acc;
    }, {})
  );
}
function mapStateToProps({
  app,
  vastTracker: { filters, counts, countTypeSelected, topNumSelected, startDate, endDate, dailyViewEnabled },
  loading,
}) {
  return {
    permissions: app.currentUser.permissions,
    loading: loading.effects['vastTracker/pageInit'],
    filters: filters[countTypeSelected]?.selected ?? [],
    selectedCounts: counts[countTypeSelected]?.items ?? [],
    topNumSelected: topNumSelected ?? 'N/A',
    countTypeSelected: countTypeSelected ?? 'N/A',
    startDate: startDate?.format('MM/DD/YYYY') ?? 'N/A',
    endDate: endDate?.format('MM/DD/YYYY') ?? 'N/A',
    dailyViewEnabled,
  };
}

export default connect(mapStateToProps)(BarChart);
